// Effektive Permissions in der Oberfläche (RBAC Phase 5).
//
// Welche Keys ein Benutzer besitzt, liefert ausschließlich der Server
// (GET /api/me/permissions). Die UI blendet danach aus — erzwungen wird im
// Backend. Hier gibt es bewusst keine Rollen: kein Rollenname, keine Matrix.

import { SCOPES, type ConditionKey, type PermissionKey, type ScopeKey } from './permissions.generated';

/** Freigabe eines Keys, wie sie der Server liefert. */
export interface EffectiveGrant {
  scope: ScopeKey;
  /** Erreichbare Standorte; `null` = alle Standorte des Mandanten. */
  standort_ids: number[] | null;
  /** Gesetzt, wenn der Key nur unter dieser Bedingung gilt (D.5) — dann ist er kein allow. */
  condition: ConditionKey | null;
  /** Limits begrenzter individueller Rechte (D.6); durchgesetzt wird serverseitig. */
  limits: Array<Record<string, unknown>> | null;
}

export type EffectivePermissions = Partial<Record<PermissionKey, EffectiveGrant>>;

export interface MePermissionsResponse {
  account_id: string;
  tenant_id: string;
  permissions_version: number;
  permissions: EffectivePermissions;
}

export interface CanOptions {
  /**
   * Standort-Kontext der Aktion. `undefined` = ohne Standortbezug; `null` = kein
   * Standort gewählt — dann nur mit einem Grant über alle Standorte.
   */
  standortId?: number | null;
  /** Bedingung, die die aufrufende Stelle ausdrücklich behandelt (D.5). */
  condition?: ConditionKey;
  /** Mindest-Scope des Grants. */
  minScope?: ScopeKey;
}

export const scopeRank = (scope: ScopeKey): number => SCOPES.indexOf(scope);

/** Default Deny: fehlende oder unbekannte Information ist kein Recht. */
export function can(
  permissions: EffectivePermissions | null | undefined,
  key: PermissionKey,
  options: CanOptions = {},
): boolean {
  const grant = permissions?.[key];
  if (!grant) return false;
  if (grant.condition && grant.condition !== options.condition) return false;
  if (options.minScope && scopeRank(grant.scope) < scopeRank(options.minScope)) return false;
  if ('standortId' in options) {
    if (grant.standort_ids === null) return true;
    const standortId = options.standortId;
    return standortId !== null && standortId !== undefined && grant.standort_ids.includes(standortId);
  }
  return true;
}

export function canAny(
  permissions: EffectivePermissions | null | undefined,
  keys: readonly PermissionKey[],
  options: CanOptions = {},
): boolean {
  return keys.some((key) => can(permissions, key, options));
}
